import { useState, useEffect } from "react";
import { social, getPlatformInfo, PLATFORMS, type SocialAccount, type FacebookPage, type InstagramAccount } from "../lib/social";
import { navigate } from "../lib/router";
import { useStaticData } from "../lib/static-data";
import { PlatformIcon } from "../components/PlatformIcon";

function PlatformBadge({ platform, size = "sm" }: { platform: string; size?: "sm" | "lg" }) {
  const info = getPlatformInfo(platform);
  const sizeClass = size === "lg" ? "w-10 h-10" : "w-7 h-7";
  const iconSize = size === "lg" ? "w-5 h-5" : "w-3.5 h-3.5";
  return (
    <span className={`${sizeClass} ${info.color} text-white rounded-xl flex items-center justify-center flex-shrink-0`}>
      <PlatformIcon platform={platform} className={iconSize} />
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400",
    pending: "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
    needs_selection: "bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400",
    reconnecting: "bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400",
    inactive: "bg-stone-100 text-stone-600 dark:bg-zinc-800 dark:text-zinc-400",
    expired: "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400",
    error: "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400",
  };
  const labels: Record<string, string> = {
    needs_selection: "select page",
    reconnecting: "reconnecting",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || colors.inactive}`}>
      {labels[status] || status}
    </span>
  );
}

/** Modal for picking a platform destination (Page, channel, or Pinterest board). */
function PagePickerModal({
  account,
  onClose,
  onSelected,
}: {
  account: SocialAccount;
  onClose: () => void;
  onSelected: () => void;
}) {
  const [pages, setPages] = useState<FacebookPage[]>([]);
  const [igAccounts, setIgAccounts] = useState<InstagramAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    social
      .listPages(account.id)
      .then((res) => {
        setPages(res.data?.pages || []);
        setIgAccounts(res.data?.instagram_accounts || []);
      })
      .catch((err: any) => setError(err.message))
      .finally(() => setLoading(false));
  }, [account.id]);

  async function handleSelectPage(pageId: string) {
    setSelecting(pageId);
    setError("");
    try {
      await social.selectPage(account.id, { page_id: pageId });
      onSelected();
    } catch (err: any) {
      setError(err.message);
      setSelecting("");
    }
  }

  async function handleSelectInstagram(igId: string) {
    setSelecting(igId);
    setError("");
    try {
      await social.selectPage(account.id, { instagram_account_id: igId });
      onSelected();
    } catch (err: any) {
      setError(err.message);
      setSelecting("");
    }
  }

  const isInstagram = account.platform === "instagram";
  const isPinterest = account.platform === "pinterest";
  const title = isInstagram
    ? "Select an Instagram Account"
    : isPinterest
      ? "Select a Pinterest Board"
      : account.platform === "facebook"
        ? "Select a Facebook Page"
        : `Select a ${getPlatformInfo(account.platform).name} destination`;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in" onClick={onClose}>
      <div
        className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col border border-stone-200/60 dark:border-zinc-800/60"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-stone-100 dark:border-zinc-800">
          <h3 className="text-lg font-semibold text-stone-900 dark:text-white">{title}</h3>
          <p className="text-sm text-stone-500 dark:text-zinc-500 mt-1">
            {isInstagram
              ? "Choose which Instagram Business account to connect."
              : isPinterest
                ? "Choose the board where SocialCast will publish Pins."
                : "Choose where you want SocialCast to publish."}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 rounded-xl mb-4 text-sm border border-red-200/60 dark:border-red-500/20">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-stone-500 dark:text-zinc-500">
              <svg className="w-4 h-4 animate-spin text-orange-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Loading pages...
            </div>
          ) : !isInstagram ? (
            pages.length === 0 ? (
              <p className="text-stone-500 dark:text-zinc-500 text-center py-8 text-sm">
                No destinations found for this account.
              </p>
            ) : (
              <div className="space-y-2">
                {pages.map((page) => (
                  <button
                    key={page.id}
                    onClick={() => handleSelectPage(page.id)}
                    disabled={!!selecting}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-stone-200/60 dark:border-zinc-800/60 hover:border-orange-300 dark:hover:border-orange-500/40 hover:bg-orange-50 dark:hover:bg-orange-500/5 transition-colors text-left disabled:opacity-50"
                  >
                    {page.picture_url ? (
                      <img src={page.picture_url} alt="" className="w-10 h-10 rounded-xl" />
                    ) : (
                      <PlatformBadge platform={account.platform} size="lg" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-stone-900 dark:text-white text-sm truncate">{page.name}</div>
                      <div className="text-xs text-stone-500 dark:text-zinc-500">
                        {isPinterest ? "Pinterest board" : page.category}{page.fan_count ? ` \u00b7 ${page.fan_count.toLocaleString()} followers` : ""}
                        {page.has_instagram && " \u00b7 Has Instagram"}
                      </div>
                    </div>
                    {selecting === page.id && (
                      <span className="text-sm text-orange-600 dark:text-orange-400 font-medium">Connecting...</span>
                    )}
                  </button>
                ))}
              </div>
            )
          ) : (
            igAccounts.length === 0 ? (
              <p className="text-stone-500 dark:text-zinc-500 text-center py-8 text-sm">
                No Instagram Business accounts found. Make sure your Facebook Pages have linked Instagram Business accounts.
              </p>
            ) : (
              <div className="space-y-2">
                {igAccounts.map((ig) => (
                  <button
                    key={ig.id}
                    onClick={() => handleSelectInstagram(ig.id)}
                    disabled={!!selecting}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-stone-200/60 dark:border-zinc-800/60 hover:border-orange-300 dark:hover:border-orange-500/40 hover:bg-orange-50 dark:hover:bg-orange-500/5 transition-colors text-left disabled:opacity-50"
                  >
                    {ig.profile_picture_url ? (
                      <img src={ig.profile_picture_url} alt="" className="w-10 h-10 rounded-full" />
                    ) : (
                      <PlatformBadge platform="instagram" size="lg" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-stone-900 dark:text-white text-sm truncate">
                        {ig.name || ig.username}
                      </div>
                      <div className="text-xs text-stone-500 dark:text-zinc-500">
                        @{ig.username}{ig.facebook_page_name ? ` \u00b7 via ${ig.facebook_page_name}` : ""}
                      </div>
                    </div>
                    {selecting === ig.id && (
                      <span className="text-sm text-orange-600 dark:text-orange-400 font-medium">Connecting...</span>
                    )}
                  </button>
                ))}
              </div>
            )
          )}
        </div>

        <div className="p-4 border-t border-stone-100 dark:border-zinc-800 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm text-stone-700 dark:text-zinc-300 hover:bg-stone-100 dark:hover:bg-zinc-800 rounded-xl transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function Accounts() {
  const ssrAccounts = useStaticData<SocialAccount[]>("accounts");
  const [accounts, setAccounts] = useState<SocialAccount[]>(ssrAccounts || []);
  const [loading, setLoading] = useState(!ssrAccounts);
  const [error, setError] = useState("");
  const [connecting, setConnecting] = useState("");
  const [reconnecting, setReconnecting] = useState<number | null>(null);
  const [pagePickerAccount, setPagePickerAccount] = useState<SocialAccount | null>(null);
  const [disconnecting, setDisconnecting] = useState<SocialAccount | null>(null);

  useEffect(() => {
    if (ssrAccounts) {
      const needsSelection = ssrAccounts.find((a) => a.status === "needs_selection");
      if (needsSelection) setPagePickerAccount(needsSelection);
    } else {
      loadAccounts();
    }

    const handler = async (e: MessageEvent) => {
      if (e.data?.type === "social.oauth_ready" && e.data?.pending_account_id) {
        await completePending(Number(e.data.pending_account_id), connecting || "");
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  async function loadAccounts() {
    try {
      const res = await social.listAccounts();
      const all = res.data || [];
      setAccounts(all);

      const needsSelection = all.find((a) => a.status === "needs_selection");
      if (needsSelection) {
        setPagePickerAccount(needsSelection);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function completePending(pendingId: number, platform: string) {
    const destinations = await social.listPages(pendingId);
    const actualPlatform = destinations.data.platform || platform;
    if (destinations.data.requires_picker) {
      setPagePickerAccount({
        id: pendingId,
        uuid: String(pendingId),
        platform: actualPlatform,
        platform_account_id: "",
        platform_username: "",
        platform_display_name: getPlatformInfo(actualPlatform).name,
        platform_avatar_url: "",
        platform_metadata: {},
        status: "needs_selection",
        status_message: "",
        last_used_at: "",
        created_at: new Date().toISOString(),
      });
    } else {
      await social.selectPage(pendingId, {});
      await loadAccounts();
    }
  }

  function watchOAuthPopup(popup: Window, pendingId: number, platform: string) {
    const timer = window.setInterval(async () => {
      if (!popup.closed) return;
      window.clearInterval(timer);
      try {
        await completePending(pendingId, platform);
      } catch (err: any) {
        setError(err.message || "Pinterest authorization did not complete");
      }
    }, 500);
  }

  async function handleConnect(platformId: string) {
    setConnecting(platformId);
    setError("");
    try {
      const res = await social.connectAccount({ platform: platformId });

      if (res.data?.auth_url) {
        const popup = window.open(res.data.auth_url, "social-connect", "width=600,height=700");
        if (!popup) {
          throw new Error("Allow popups to connect your social account.");
        }
        watchOAuthPopup(popup, res.data.pending_account_id, res.data.platform);
      } else {
        await completePending(res.data.pending_account_id, res.data.platform);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setConnecting("");
    }
  }

  async function handleReconnect(account: SocialAccount) {
    setReconnecting(account.id);
    setError("");
    try {
      const res = await social.reconnectAccount(account.id);

      if (res.data?.auth_url) {
        const popup = window.open(res.data.auth_url, "social-reconnect", "width=600,height=700");
        if (!popup) {
          throw new Error("Allow popups to reconnect your social account.");
        }
        watchOAuthPopup(popup, res.data.pending_account_id, res.data.platform);
      } else {
        await completePending(res.data.pending_account_id, res.data.platform);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setReconnecting(null);
    }
  }

  async function confirmDisconnect() {
    if (!disconnecting) return;
    try {
      await social.disconnectAccount(disconnecting.id);
      await loadAccounts();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDisconnecting(null);
    }
  }

  const activeAccounts = accounts.filter((a) => a.status === "active" || a.status === "reconnecting");
  const troubledAccounts = accounts.filter((a) => a.status === "expired" || a.status === "error");
  const pendingAccounts = accounts.filter((a) => a.status === "needs_selection");
  const visibleAccounts = [...activeAccounts, ...troubledAccounts, ...pendingAccounts];
  const connectedPlatforms = new Set([...activeAccounts, ...troubledAccounts].map((a) => a.platform));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-stone-900 dark:text-white">Accounts</h2>
          <p className="text-sm text-stone-500 dark:text-zinc-500 mt-1">
            {activeAccounts.length} connected account{activeAccounts.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 rounded-xl mb-4 text-sm border border-red-200/60 dark:border-red-500/20">
          {error}
        </div>
      )}

      {/* Connected & needs_selection Accounts */}
      {loading ? (
        <div className="flex items-center gap-2 text-stone-500 dark:text-zinc-500">
          <svg className="w-4 h-4 animate-spin text-orange-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          Loading accounts...
        </div>
      ) : visibleAccounts.length > 0 ? (
        <div className="space-y-3 mb-8">
          {visibleAccounts.map((account) => (
            <div
              key={account.id}
              className="flex items-center gap-4 p-4 bg-white dark:bg-zinc-900/50 rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 hover:border-stone-300 dark:hover:border-zinc-700 transition-colors"
            >
              {account.platform_avatar_url ? (
                <img src={account.platform_avatar_url} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <PlatformBadge platform={account.platform} size="lg" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <PlatformBadge platform={account.platform} />
                  <span className="font-medium text-stone-900 dark:text-white truncate">
                    {account.platform_display_name || account.platform_username || account.platform}
                  </span>
                  <StatusBadge status={account.status} />
                </div>
                {account.platform_username && (
                  <p className="text-sm text-stone-500 dark:text-zinc-500 mt-0.5">{account.platform_username}</p>
                )}
                {account.status === "needs_selection" && (
                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                    Select a page or account to finish setup
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {account.status === "needs_selection" && (
                  <button
                    onClick={() => setPagePickerAccount(account)}
                    className="px-3 py-1.5 text-sm bg-orange-500 text-white hover:bg-orange-600 rounded-xl font-medium transition-colors"
                  >
                    Select Page
                  </button>
                )}
                {account.last_used_at && (
                  <span className="text-xs text-stone-400 dark:text-zinc-600 hidden sm:inline">
                    Last used {new Date(account.last_used_at).toLocaleDateString()}
                  </span>
                )}
                {account.platform !== "telegram" && account.status !== "needs_selection" && account.status !== "reconnecting" && (
                  <button
                    onClick={() => handleReconnect(account)}
                    disabled={reconnecting === account.id}
                    className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-xl transition-colors disabled:opacity-50"
                  >
                    {reconnecting === account.id ? "Reconnecting..." : "Reconnect"}
                  </button>
                )}
                {account.status === "reconnecting" && (
                  <span className="px-3 py-1.5 text-sm text-blue-500 dark:text-blue-400 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Awaiting re-auth...
                  </span>
                )}
                <button
                  onClick={() => setDisconnecting(account)}
                  className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl transition-colors"
                >
                  Disconnect
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Connect New Account */}
      <div>
        <h3 className="text-xs font-semibold text-stone-400 dark:text-zinc-600 uppercase tracking-wider mb-3">Connect a Platform</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PLATFORMS.map((platform) => {
            const isConnected = connectedPlatforms.has(platform.id);
            const isConnecting = connecting === platform.id;
            return (
              <button
                key={platform.id}
                onClick={() => handleConnect(platform.id)}
                disabled={isConnecting}
                className={`flex items-center gap-3 p-4 rounded-2xl border transition-all text-left ${
                  isConnected
                    ? "border-emerald-200/60 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/5"
                    : "border-stone-200/60 dark:border-zinc-800/60 bg-white dark:bg-zinc-900/50 hover:border-stone-300 dark:hover:border-zinc-700 hover:shadow-sm"
                } disabled:opacity-50`}
              >
                <PlatformBadge platform={platform.id} size="lg" />
                <div className="flex-1">
                  <div className="font-medium text-stone-900 dark:text-white text-sm">{platform.name}</div>
                  <div className="text-xs text-stone-500 dark:text-zinc-500">
                    {isConnecting ? "Connecting..." : isConnected ? "Connected" : "Click to connect"}
                  </div>
                </div>
                {isConnected && (
                  <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Disconnect confirmation modal */}
      {disconnecting && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in" onClick={() => setDisconnecting(null)}>
          <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-sm border border-stone-200/60 dark:border-zinc-800/60" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-stone-900 dark:text-white mb-2">Disconnect Account</h3>
              <p className="text-sm text-stone-500 dark:text-zinc-400">
                Disconnect <span className="font-medium text-stone-700 dark:text-zinc-300">{disconnecting.platform_display_name || disconnecting.platform_username || disconnecting.platform}</span>? You can reconnect it later.
              </p>
            </div>
            <div className="p-6 pt-0 flex gap-3">
              <button onClick={() => setDisconnecting(null)} className="flex-1 px-4 py-2.5 text-sm text-stone-700 dark:text-zinc-300 hover:bg-stone-100 dark:hover:bg-zinc-800 rounded-xl transition-colors border border-stone-200 dark:border-zinc-700">
                Cancel
              </button>
              <button onClick={confirmDisconnect} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors">
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Picker Modal */}
      {pagePickerAccount && (
        <PagePickerModal
          account={pagePickerAccount}
          onClose={() => setPagePickerAccount(null)}
          onSelected={() => {
            setPagePickerAccount(null);
            loadAccounts();
          }}
        />
      )}
    </div>
  );
}
